<template>
  <div class="gray-section p-3 border-round-2xl">
    <div :class="[`container ${deviceType}-container`]" :style="styleObject">
      <template v-for="n in 6" :key="n">
        <LineChart
          v-if="checkType(n, 'Lineas')"
          :chart="graficos[n - 1]"
          :class="chartClass(n)"
          ref="charts"
        />
        <BarChart
          v-if="
            checkType(n, 'Barras verticales') ||
            checkType(n, 'Barras horizontales')
          "
          :chart="graficos[n - 1]"
          :class="chartClass(n)"
          :orientation="orientation(n)"
          ref="charts"
        />
        <PieChart
          v-if="checkType(n, 'Tarta')"
          :chart="graficos[n - 1]"
          :class="chartClass(n)"
          ref="charts"
        />
        <SankeyChart
          v-if="checkType(n, 'Sankey')"
          :chart="graficos[n - 1]"
          :class="chartClass(n)"
          ref="charts"
        />
        <CircularSankeyChart
          v-if="checkType(n, 'Sankey circular')"
          :chart="graficos[n - 1]"
          :class="chartClass(n)"
          ref="charts"
        />
        <RadarChart
          v-if="checkType(n, 'Radar')"
          :chart="graficos[n - 1]"
          :class="chartClass(n)"
          ref="charts"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, reactive, nextTick } from "vue";
import * as d3 from "d3";

import LineChart from "@/pages/_root/views/reports-details/chart-type/LineChart.vue"; // eslint-disable-line
import BarChart from "@/pages/_root/views/reports-details/chart-type/BarChart.vue"; // eslint-disable-line
import PieChart from "@/pages/_root/views/reports-details/chart-type/PieChart.vue"; // eslint-disable-line
import SankeyChart from "@/pages/_root/views/reports-details/chart-type/SankeyChart.vue"; // eslint-disable-line
import CircularSankeyChart from "@/pages/_root/views/reports-details/chart-type/CircularSankeyChart.vue"; // eslint-disable-line
import RadarChart from "@/pages/_root/views/reports-details/chart-type/RadarChart.vue"; // eslint-disable-line

// #region Props, Emits
const props = defineProps({
  graficos: {
    type: Array,
    required: true,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const deviceType = ref("desktop");
const areasStyle = ref(null);
const styleObject = reactive({
  gridTemplateAreas: areasStyle,
});
const charts = ref([]);
const windowWidth = ref(0);
// #endregion

// #region Functions
onMounted(() => {
  setDeviceType();

  nextTick(() => {
    loadVariables();
  });

  // listen to window resize
  window.addEventListener("resize", () => {
    setDeviceType();

    // hide the graphs
    for (let i = 1; i <= 6; i++) {
      const id = `#container-${i}`;
      const container = d3.select(id);

      container.select("svg").remove();
    }

    nextTick(() => {
      loadVariables();
    });
  });
});

// Call loadVariables for each chart
function loadVariables() {
  if (window.innerWidth == windowWidth.value) return;

  windowWidth.value = window.innerWidth;

  charts.value.forEach((chart, index) => {
    const grafico = props.graficos[index];

    if (grafico && grafico.tipo != "Radar") {
      try {
        const interval = setInterval(() => {
          const graphContainerValue = chart.graphContainer;

          if (graphContainerValue) {
            // graphContainer width and height
            const graphContainerWidth = graphContainerValue.clientWidth;
            const graphContainerHeight = graphContainerValue.clientHeight;

            if (graphContainerWidth && graphContainerHeight) {
              clearInterval(interval);

              const id = `#container-${grafico.Id}`;

              chart.loadGraph(id, graphContainerWidth, graphContainerHeight);
            }
          }
        });
      } catch (error) {
        console.error("Error loading graph variables", error);

        clearInterval(interval);
      }
    }
  });
}

function chartClass(index) {
  return `area area-${index} border-round-xl`;
}

function setDeviceType() {
  const windowWidth = window.innerWidth;

  if (windowWidth < 768) {
    deviceType.value = "mobile";
  } else if (windowWidth < 1024) {
    deviceType.value = "tablet";
  } else {
    deviceType.value = "desktop";
  }

  formatAreas();
}

function formatAreas() {
  let areas = [0, 0, 0, 0, 0, 0];

  props.graficos.forEach((grafico, i) => {
    const posiciones = grafico.posiciones;

    posiciones.forEach((posicion) => {
      if (posicion.type == deviceType.value) {
        posicion.areas.forEach((area) => {
          areas[area - 1] = i + 1;
        });
      }
    });
  });

  switch (deviceType.value) {
    case "desktop":
      areasStyle.value = `
        "area-${areas[0]} area-${areas[1]} area-${areas[2]}"
        "area-${areas[3]} area-${areas[4]} area-${areas[5]}"
        `;

      break;

    case "tablet":
      areasStyle.value = `
        "area-${areas[0]} area-${areas[1]}"
        "area-${areas[2]} area-${areas[3]}"
        "area-${areas[4]} area-${areas[5]}"
        `;

      break;

    case "mobile":
      areasStyle.value = `
        "area-${areas[0]}"
        "area-${areas[1]}"
        "area-${areas[2]}"
        "area-${areas[3]}"
        "area-${areas[4]}"
        "area-${areas[5]}"
        `;

      break;
  }

  // remove the starting and ending spaces
  areasStyle.value = areasStyle.value.trim();

  // Replace "area-0" by "."
  areasStyle.value = areasStyle.value.replace(/area-0/g, ".");
}

function checkType(index, type) {
  const graph = props.graficos[index - 1];

  if (graph) {
    return type == graph.tipo;
  }
}

function orientation(index) {
  return props.graficos[index - 1].tipo == "Barras verticales"
    ? "vertical"
    : "horizontal";
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.container {
  display: grid;
  grid-auto-flow: row;
  gap: 1rem;

  &.desktop-container {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 20rem 20rem;
  }

  &.tablet-container {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 20rem 20rem 20rem;
  }

  &.mobile-container {
    grid-template-columns: 1fr;
    grid-template-rows: 20rem 20rem 20rem 20rem 20rem 20rem;
  }

  .area {
    border: 1px solid $primary;
    background-color: white;

    &-1 {
      grid-area: area-1;
    }

    &-2 {
      grid-area: area-2;
    }

    &-3 {
      grid-area: area-3;
    }

    &-4 {
      grid-area: area-4;
    }

    &-5 {
      grid-area: area-5;
    }

    &-6 {
      grid-area: area-6;
    }
  }
}
</style>
